import { node, oneOf } from 'prop-types';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { AuthContext, Icon } from 'components';
import { routes } from 'router';
import { appService } from 'services';
import { popStep, resetAuthStore } from 'store';

import beachIllustration from 'assets/img/beach-illustration.jpeg';
import 'assets/styles/components/auth/auth-layout.scss';

const AuthLayout = ({ children, type }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const steps = useSelector((state) => state.auth.steps);
  const { error, setError } = useContext(AuthContext);
  const isRedirection = appService.isRedirection();
  const showBackLink = type !== 'login' && [...steps].pop() !== 'confirmationStep';

  const handleChangeStep = () => {
    setError({ ...error, [type]: '' });
    if (steps.length === 1) {
      navigate(routes.login.path, { replace: true });
    } else {
      dispatch(popStep());
    }
  };

  const redirectToLogin = () => {
    dispatch(resetAuthStore());
    navigate(routes.login.path, { replace: true });
  };

  return (
    <section className="auth-layout">
      <div className="image-container relative aspect-[3/2] lg:aspect-auto">
        {appService.isTouloisirs() && (
          <img
            className="desktop-logo"
            src={appService.loadBrandImage('logoDark')}
            alt={t(`images.logoAlt${appService.brand()}`)}
            width="240"
            height="86"
          />
        )}

        <img
          className="image"
          src={beachIllustration}
          alt={t('auth:images.beachIllustrationAlt')}
          width="644"
          height="802"
        />
      </div>

      <div className="content-card">
        {!isRedirection && (
          <>
            {type === 'login' ? (
              <Link
                to={routes.registration.path}
                data-test-id="signinButton"
                className="btn secondary hidden w-fit ml-auto lg:block"
              >
                {t('auth:common.signinButton')}
              </Link>
            ) : (
              <button
                type="button"
                className="text-gray-700 underline underline-offset-2 block ml-auto hover:text-primary-500"
                onClick={redirectToLogin}
              >
                {t('auth:registration.haveAccount', { brand: appService.isTouloisirs() ? appService.brand() : '' })}
              </button>
            )}
          </>
        )}

        <div className="grow flex flex-col justify-center lg:pr-12 2xl:pr-32">
          {showBackLink && (
            <button type="button" className="back-link" onClick={handleChangeStep}>
              <Icon name="angle-left"></Icon>
              <span className="text-gray-800">{t('auth:common.backLink')}</span>
            </button>
          )}

          {children}
        </div>
      </div>
    </section>
  );
};

AuthLayout.propTypes = {
  children: node.isRequired,
  type: oneOf(['login', 'forgotPassword', 'registration', 'arrivajRegistration', 'updatePassword']).isRequired
};

export default AuthLayout;
